import { useState, useMemo } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, LogOut, UserCog } from 'lucide-react';
import {
  navItems,
  superadminNavItems,
  adminNavItems,
  ChevronDown,
  ChevronUp,
  type NavItem,
} from './nav-items';
import { Button } from '@/components/ui/button';
import { useUser } from '@/stores';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { impersonation } from '@/lib/api-client';
import { useStopImpersonation } from '@/features/admin/hooks';
import { usePendingApprovalCount } from '@/features/hrms/leaves/hooks/useLeaves';
import { useLeavePermissions } from '@/features/hrms/leaves/hooks/useLeavePermissions';
import { useCurrentTenant } from '@/features/tenant/hooks/useTenant';
import { getActiveChildHref, isPathWithin, type NavigationAliases } from './navigation-state';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}

export function Sidebar({ collapsed: isCollapsed, onToggle, className }: SidebarProps) {
  const location = useLocation();
  const user = useUser();
  const userInitials = user
    ? `${user.firstName?.trim()?.[0] ?? ''}${user.lastName?.trim()?.[0] ?? ''}`.toUpperCase() ||
      user.email?.trim()?.[0]?.toUpperCase() ||
      'U'
    : '';
  const {
    hasPermission,
    hasAnyPermission,
    hasModuleAccess,
    hasRole,
    isSuperAdmin: isSuperAdminRole,
  } = usePermissions();
  const [expandedItemOverride, setExpandedItemOverride] = useState<{
    pathname: string;
    title: string | null;
  } | null>(null);
  const stopImpersonation = useStopImpersonation();

  const isImpersonating = impersonation.isImpersonating();
  const isSuperAdmin = !isImpersonating && isSuperAdminRole;

  const { data: tenant } = useCurrentTenant({ enabled: !isSuperAdmin });
  const tenantBranding = tenant as unknown as { logoUrl?: string; name?: string } | undefined;
  const tenantLogo = tenantBranding?.logoUrl;
  const tenantName = tenantBranding?.name ?? tenant?.name;

  const { canApproveRequests, getMaxApproveScope } = useLeavePermissions({
    enabled: !isSuperAdmin,
  });
  const maxApproveScope = canApproveRequests ? getMaxApproveScope() : 'self';
  const pendingApprovalTeamView = maxApproveScope === 'self' ? undefined : maxApproveScope;
  // Pending approval badge for Leave > Requests
  const { data: pendingCount } = usePendingApprovalCount({
    enabled:
      !isSuperAdmin &&
      canApproveRequests &&
      pendingApprovalTeamView !== undefined &&
      Boolean(user?.employeeId),
    teamView: pendingApprovalTeamView,
  });

  const computedNavItems = useMemo(() => {
    return navItems.map((item) => {
      if (item.title !== 'Leave' || !item.children) return item;
      return {
        ...item,
        children: item.children.map((child) => {
          if (child.title !== 'Requests' || !canApproveRequests || !pendingCount) return child;
          return { ...child, badge: String(pendingCount) };
        }),
      };
    });
  }, [canApproveRequests, pendingCount]);

  const navigationAliases = useMemo<NavigationAliases>(
    () => ({
      '/employees/me': user?.employeeId ? [`/employees/${user.employeeId}`] : [],
    }),
    [user?.employeeId],
  );

  const activeChildHref = (item: NavItem) =>
    getActiveChildHref(location.pathname, item.children, navigationAliases);

  const isChildActive = (parent: NavItem, child: NavItem) => activeChildHref(parent) === child.href;

  // Find the parent item that contains the current active route
  const findActiveParent = (() => {
    const allNavItems = [...computedNavItems, ...adminNavItems, ...superadminNavItems];
    for (const item of allNavItems) {
      if (item.children) {
        // Check if any child href or activePaths matches the current path
        const hasActiveChild = Boolean(activeChildHref(item));
        if (hasActiveChild) return item.title;

        // Also check if the pathname falls under the parent's href scope
        // e.g. Settings (href: /settings) stays expanded for /settings/preferences
        if (isPathWithin(location.pathname, item.href)) {
          return item.title;
        }
      }
    }
    return null;
  })();

  const expandedItem =
    expandedItemOverride?.pathname === location.pathname
      ? expandedItemOverride.title
      : findActiveParent;

  // Filter function that checks module, permission, anyPermission, and requiredRole
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return (
      items
        .filter((item) => {
          // Check role requirement first
          if (item.requiredRole) {
            if (!hasRole(item.requiredRole)) return false;
          }

          // Module-level access check
          if (item.module && !hasModuleAccess(item.module)) {
            return false;
          }

          // Specific permission check
          if (item.permission && !hasPermission(item.permission)) {
            return false;
          }

          // Any of these permissions (OR logic)
          if (item.anyPermission && item.anyPermission.length > 0) {
            if (!hasAnyPermission(item.anyPermission)) {
              return false;
            }
          }

          return true;
        })
        .map((item) => ({
          ...item,
          // Also filter children
          children: item.children ? filterNavItems(item.children) : undefined,
        }))
        // Remove parent items that have no visible children (if they had children before)
        .filter((item) => {
          // If item originally had children but now has none, hide it
          const originalItem = items.find((i) => i.href === item.href);
          if (originalItem?.children && originalItem.children.length > 0) {
            return item.children && item.children.length > 0;
          }
          return true;
        })
    );
  };

  // Choose nav items based on user role
  // SuperAdmin sees admin-specific navigation, others see tenant navigation
  // When impersonating, always show tenant navigation
  const baseNavItems = isSuperAdmin ? superadminNavItems : [...computedNavItems, ...adminNavItems];

  // Filter nav items based on permissions
  const visibleNavItems = filterNavItems(baseNavItems);

  // Accordion behavior: toggle item, close others
  const toggleExpanded = (title: string) => {
    setExpandedItemOverride((prev) => {
      const currentExpanded = prev?.pathname === location.pathname ? prev.title : findActiveParent;
      return {
        pathname: location.pathname,
        title: currentExpanded === title ? null : title,
      };
    });
  };

  const isItemActive = (item: NavItem): boolean => {
    if (item.children) {
      return Boolean(activeChildHref(item)) || isPathWithin(location.pathname, item.href);
    }
    return isPathWithin(location.pathname, item.href);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300 hidden lg:flex lg:flex-col',
        isCollapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      {/* Logo Section - Fixed */}
      <div className="flex h-16 items-center justify-between border-b px-4 flex-shrink-0">
        {!isCollapsed && (
          <Link to="/" className="flex items-center gap-2 min-w-0">
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={tenantName || 'Company logo'}
                className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">U</span>
              </div>
            )}
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent truncate">
              {tenantName || 'UniTree'}
            </span>
          </Link>
        )}

        {isCollapsed &&
          (tenantLogo ? (
            <img
              src={tenantLogo}
              alt={tenantName || 'Company logo'}
              className="w-8 h-8 rounded-lg object-cover mx-auto"
            />
          ) : (
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center mx-auto">
              <span className="text-white text-sm font-bold">U</span>
            </div>
          ))}
      </div>

      {/* Impersonation Banner */}
      {isImpersonating && !isCollapsed && (
        <div className="mx-2 mt-2 p-2 bg-amber-500/10 border border-amber-500/50 rounded-lg">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <UserCog className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs font-medium">Impersonating Tenant</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1 h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/20"
            onClick={() => stopImpersonation.mutate()}
            disabled={stopImpersonation.isPending}
          >
            <LogOut className="h-3 w-3 mr-1" />
            Exit Impersonation
          </Button>
        </div>
      )}

      {isImpersonating && isCollapsed && (
        <div className="mx-2 mt-2 p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-500/20"
            onClick={() => stopImpersonation.mutate()}
            disabled={stopImpersonation.isPending}
            title="Exit Impersonation"
          >
            <UserCog className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = isItemActive(item);
          const isExpanded = expandedItem === item.title;
          const hasChildren = item.children && item.children.length > 0;
          const firstChildHref = item.children?.[0]?.href;

          // For items with children, render a collapsible section
          if (hasChildren && !isCollapsed) {
            return (
              <Collapsible
                key={item.href}
                open={isExpanded}
                onOpenChange={() => toggleExpanded(item.title)}
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent',
                      isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.title}</span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 mt-1 space-y-0.5">
                  {item.children?.map((child) => {
                    const ChildIcon = child.icon;
                    const isActiveChild = isChildActive(item, child);
                    return (
                      <div key={child.href}>
                        {child.sectionLabel && (
                          <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                            {child.sectionLabel}
                          </div>
                        )}
                        <Link
                          to={child.href}
                          activeOptions={{ exact: true }}
                          aria-current={isActiveChild ? 'page' : undefined}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent',
                            isActiveChild
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          <ChildIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{child.title}</span>
                          {child.badge && (
                            <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold">
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      </div>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          }

          // For collapsed sidebar with children, just show the parent icon linking to first child
          if (hasChildren && isCollapsed && firstChildHref) {
            return (
              <Link
                key={item.href}
                to={firstChildHref}
                activeOptions={{ exact: true }}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent justify-center',
                  isActive
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title={item.title}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
              </Link>
            );
          }

          // Regular nav item without children
          return (
            <Link
              key={item.href}
              to={item.href}
              activeOptions={{ exact: true }}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent',
                isActive
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'text-muted-foreground hover:text-foreground',
                isCollapsed && 'justify-center',
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && (
                <>
                  <span className="flex-1">{item.title}</span>
                  {item.badge && (
                    <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Toggle Button - Fixed */}
      <div className="border-t p-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className={cn('w-full', isCollapsed && 'px-2')}
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5 mr-2" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </div>

      {/* User Info (Bottom) - Fixed */}
      {!isCollapsed && user && (
        <div className="border-t p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-semibold">
              {userInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.fullName}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
